import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useT, LANGUAGES } from '../i18n';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import CurrencyToggle from '../components/shared/CurrencyToggle';
import { CURRENCIES } from '../utils/helpers';
import { DOC_TYPES, planPricing, billingCurrency, saveBillingCurrency } from '../utils/helpers';
import api from '../utils/api';
import versionInfo from '../version.json';

const FEATURE_KEYS = [
  { key: 'aiExtract',     icon: '✨', highlight: true },
  { key: 'quotations',    icon: '📋' },
  { key: 'invoices',      icon: '🧾' },
  { key: 'receipts',      icon: '✅' },
  { key: 'bills',         icon: '💳' },
  { key: 'purchase',      icon: '📦' },
  { key: 'credit',        icon: '↩️' },
  { key: 'clients',       icon: '👥' },
  { key: 'multiCurrency', icon: '🌍' },
  { key: 'export',        icon: '🖨' },
];

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, isRTL } = useT();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Live plan prices from the server (falls back to the list prices until it answers)
  const [billing, setBilling] = useState(null);
  const [pickedCurrency, setPickedCurrency] = useState(null);
  useEffect(() => {
    api.get('/stripe/config').then(r => setBilling(r.data)).catch(() => {});
  }, []);
  const price = planPricing(billing, pickedCurrency || billingCurrency(billing));
  const pickCurrency = c => { setPickedCurrency(c); saveBillingCurrency(c); };

  const goSignup = () => navigate('/login?mode=register');
  const goLogin  = () => navigate('/login');

  const currencyCount = CURRENCIES.length;
  const languageCount = LANGUAGES.length;
  const docTypeCount  = DOC_TYPES.length;

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-12px) } }
        @keyframes fadeUp { from { opacity:0; transform: translateY(16px) } to { opacity:1; transform: translateY(0) } }
        .landing-fadeup { animation: fadeUp 0.6s ease both; }
        .landing-link { transition: color 0.15s; }
        .landing-link:hover { color: var(--accent) !important; }
        .landing-feature:hover { transform: translateY(-4px); border-color: var(--blue) !important; }
        .landing-feature { transition: transform 0.2s, border-color 0.2s; }
        html { scroll-behavior: smooth; }
      `}</style>

      {/* ── Sticky Nav ─────────────────────────────── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: scrolled ? 'var(--card-bg)' : 'transparent',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        backdropFilter: scrolled ? 'saturate(180%) blur(10px)' : 'none',
        transition: 'background 0.2s, border-color 0.2s',
      }}>
        <div style={navInner}>
          <Link to="/" style={{ display: 'flex', alignItems: 'baseline', textDecoration: 'none' }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B', letterSpacing: '-1.5px' }}>I</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: scrolled ? 'var(--heading)' : '#fff', letterSpacing: '-1px' }}>nvio</span>
          </Link>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="#ai"       className="landing-link" style={{ ...navLink, color: scrolled ? 'var(--accent)' : '#F59E0B', fontWeight: 700 }}>✨ AI Import</a>
            <a href="#features" className="landing-link" style={{ ...navLink, color: scrolled ? 'var(--text-muted)' : 'rgba(255,255,255,0.85)' }}>{t('nav.features')}</a>
            <a href="#pricing"  className="landing-link" style={{ ...navLink, color: scrolled ? 'var(--text-muted)' : 'rgba(255,255,255,0.85)' }}>{t('nav.pricing')}</a>
            <LanguageSwitcher variant={scrolled ? 'light' : 'dark'} compact />
            <button onClick={toggleTheme} aria-label="Toggle theme" style={{
              background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16,
              color: scrolled ? 'var(--text-muted)' : 'rgba(255,255,255,0.85)', padding: 4,
            }}>{theme === 'dark' ? '☀' : '🌙'}</button>
            <button onClick={goLogin} style={{
              background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              color: scrolled ? 'var(--heading)' : '#fff', fontFamily: 'inherit', padding: '6px 12px',
            }}>{t('nav.signIn')}</button>
            <button onClick={goSignup} style={ctaBtn}>{t('nav.getStarted')} {isRTL ? '←' : '→'}</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────── */}
      <section style={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(160deg, #0f2447 0%, #1A3C6E 50%, #2E75B6 100%)',
        color: '#fff', padding: '64px 24px 96px', marginTop: -64,
      }}>
        <div style={{ position: 'absolute', top: '20%', left: '-8%', width: 320, height: 320, borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(40px)', animation: 'float 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '-10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'rgba(46,117,182,0.25)', filter: 'blur(50px)', animation: 'float 10s ease-in-out infinite reverse' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', paddingTop: 80, position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 48, alignItems: 'center' }}>

            <div className="landing-fadeup">
              <div style={{
                display: 'inline-block', padding: '5px 12px', background: 'rgba(245,158,11,0.18)',
                color: '#F59E0B', fontSize: 12, fontWeight: 600, borderRadius: 20, letterSpacing: '0.5px',
                textTransform: 'uppercase', marginBottom: 20,
              }}>
                {t('hero.eyebrow')}
              </div>
              <h1 style={{
                fontSize: 'clamp(34px, 5vw, 56px)', fontWeight: 800, lineHeight: 1.1,
                letterSpacing: '-1.5px', marginBottom: 18, color: '#fff',
              }}>
                {t('hero.headline1')}<br />{t('hero.headline2')}<br />
                <span style={{ color: '#F59E0B' }}>{t('hero.headline3')}</span>
              </h1>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)', marginBottom: 28, maxWidth: 480 }}>
                {t('hero.sub')}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <button onClick={goSignup} style={heroCta}>{t('hero.ctaPrimary')}</button>
                <a href="#pricing" style={heroCtaGhost}>{t('hero.ctaSecondary')}</a>
              </div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                {t('hero.trust')}
              </p>
            </div>

            <div className="landing-fadeup" style={{ animationDelay: '0.15s' }}>
              <InvoiceMockup />
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div style={{ maxWidth: 1100, margin: '64px auto 0', position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 20, padding: '24px', background: 'rgba(255,255,255,0.06)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}>
            {[
              { v: docTypeCount,  l: t('stats.docTypes') },
              { v: currencyCount, l: t('stats.currencies') },
              { v: languageCount, l: t('stats.languages') },
              { v: '∞',           l: t('stats.docsPerMo') },
              { v: '30',          l: t('stats.dayTrial') },
            ].map(s => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#F59E0B', lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Document Import (highlighted) ───────── */}
      <section id="ai" style={{ padding: '88px 24px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
            gap: 48, alignItems: 'center',
            background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(46,117,182,0.06) 100%)',
            border: '1px solid var(--border)', borderRadius: 20, padding: 'clamp(28px, 4vw, 56px)',
          }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', background: 'rgba(245,158,11,0.18)',
                color: '#F59E0B', fontSize: 12, fontWeight: 700, borderRadius: 20,
                letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 16,
              }}>
                <span>✨</span> {t('ai.eyebrow')}
              </div>
              <h2 style={{ ...sectionH, marginBottom: 14 }}>{t('ai.title')}</h2>
              <p style={{ fontSize: 16, color: 'var(--text-muted)', lineHeight: 1.65, marginBottom: 22 }}>
                {t('ai.sub')}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: 26 }}>
                {['ai.bullet1','ai.bullet2','ai.bullet3'].map(k => (
                  <li key={k} style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 14, color: 'var(--text)' }}>
                    <span style={{ color: 'var(--success)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                    {t(k)}
                  </li>
                ))}
              </ul>
              <button onClick={goSignup} style={{
                background: 'var(--accent)', color: '#1A3C6E', border: 'none', cursor: 'pointer',
                padding: '12px 22px', borderRadius: 10, fontWeight: 700, fontSize: 14,
                fontFamily: 'inherit', boxShadow: '0 8px 24px rgba(245,158,11,0.3)',
              }}>
                {t('ai.cta')} {isRTL ? '←' : '→'}
              </button>
            </div>

            <ExtractMockup t={t} />
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────── */}
      <section id="features" style={{ padding: '88px 24px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={eyebrow}>{t('features.eyebrow')}</div>
            <h2 style={sectionH}>{t('features.title')}</h2>
            <p style={sectionSub}>{t('features.sub')}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 20 }}>
            {FEATURE_KEYS.map(f => (
              <div key={f.key} className="landing-feature" style={{
                background: 'var(--card-bg)',
                border: f.highlight ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                borderRadius: 14, padding: '26px 24px', boxShadow: 'var(--shadow)',
                position: 'relative',
              }}>
                {f.highlight && (
                  <div style={{
                    position: 'absolute', top: -10, right: 16,
                    background: 'var(--accent)', color: '#1A3C6E',
                    fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 12,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>NEW · AI</div>
                )}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, marginBottom: 14,
                  background: f.highlight ? 'rgba(245,158,11,0.18)' : 'rgba(46,117,182,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--heading)', marginBottom: 6 }}>
                  {t(`features.items.${f.key}.title`)}
                </h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {t(`features.items.${f.key}.desc`, { count: currencyCount })}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────── */}
      <section id="pricing" style={{ padding: '88px 24px', background: 'var(--hover-bg)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={eyebrow}>{t('pricing.eyebrow')}</div>
            <h2 style={sectionH}>{t('pricing.title')}</h2>
            <p style={sectionSub}>{t('pricing.sub')}</p>
            <div style={{ marginTop: 20 }}>
              <CurrencyToggle currencies={price.currencies} value={price.currency} onChange={pickCurrency} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 20 }}>
            <PlanCard
              name={t('pricing.monthly')}
              price={price.monthly}
              cadence={t('pricing.perMonth')}
              features={['f1','f2','f3','f4'].map(k => t(`pricing.plan.monthly.${k}`))}
              cta={t('pricing.cta')}
              onClick={goSignup}
            />
            <PlanCard
              name={t('pricing.yearly')}
              price={price.yearly}
              cadence={t('pricing.perYear')}
              badge={price.savePct > 0 ? t('pricing.save', { p: price.savePct }) : null}
              features={['f1','f2','f3','f4'].map(k => t(`pricing.plan.yearly.${k}`, { amount: price.yearlyPerMonth }))}
              cta={t('pricing.cta')}
              onClick={goSignup}
              highlighted
            />
            <PlanCard
              name={t('pricing.custom')}
              price={t('pricing.customPrice')}
              cadence={t('pricing.customSub')}
              features={['plan.custom.f1','plan.custom.f2','plan.custom.f3','plan.custom.f4'].map(k => t(`pricing.${k}`))}
              cta={t('pricing.contactUs')}
              onClick={() => { document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              variant="contact"
            />
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────── */}
      <section style={{
        padding: '88px 24px',
        background: 'linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)',
        color: '#fff', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 14 }}>
            {t('finalCta.title')}
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', marginBottom: 28, lineHeight: 1.6 }}>
            {t('finalCta.sub')}
          </p>
          <button onClick={goSignup} style={{ ...heroCta, background: '#F59E0B', color: '#1A3C6E' }}>
            {t('finalCta.button')}
          </button>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────── */}
      <footer style={{ background: 'var(--card-bg)', borderTop: '1px solid var(--border)', padding: '40px 24px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 32 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 900, color: '#F59E0B', letterSpacing: '-1.5px' }}>I</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)', letterSpacing: '-1px' }}>nvio</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{t('footer.tagline')}</p>
          </div>
          <div>
            <div style={footerH}>{t('footer.product')}</div>
            <a href="#features" className="landing-link" style={footerLink}>{t('nav.features')}</a>
            <a href="#pricing"  className="landing-link" style={footerLink}>{t('nav.pricing')}</a>
            <Link to="/login"   className="landing-link" style={footerLink}>{t('nav.signIn')}</Link>
          </div>
          <div>
            <div style={footerH}>{t('footer.account')}</div>
            <Link to="/login?mode=register" className="landing-link" style={footerLink}>{t('footer.createAccount')}</Link>
            <Link to="/login"               className="landing-link" style={footerLink}>{t('nav.signIn')}</Link>
          </div>
          <div>
            <div style={footerH}>{t('footer.legal')}</div>
            <Link to="/terms"   className="landing-link" style={footerLink}>{t('footer.terms')}</Link>
            <Link to="/privacy" className="landing-link" style={footerLink}>{t('footer.privacy')}</Link>
            <Link to="/refund"  className="landing-link" style={footerLink}>{t('footer.refund')}</Link>
          </div>
          <div id="contact" style={{ scrollMarginTop: 80 }}>
            <div style={footerH}>{t('footer.contact')}</div>
            <a href="mailto:marketing@alaganu.com" className="landing-link" style={{ ...footerLink, display: 'flex', alignItems: 'center', gap: 8 }} dir="ltr">
              <span style={{ fontSize: 14 }}>✉</span> marketing@alaganu.com
            </a>
            <a href="https://wa.me/60106650171" target="_blank" rel="noreferrer" className="landing-link" style={{ ...footerLink, display: 'flex', alignItems: 'center', gap: 8 }} dir="ltr">
              <span style={{ fontSize: 14 }}>💬</span> +60 10 665 0171
            </a>
            <div style={{ marginTop: 12 }}><LanguageSwitcher /></div>
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '32px auto 0', paddingTop: 20, borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <p style={{ fontSize: 12, color: 'var(--text-subtle)' }}>v{versionInfo.version} · build {versionInfo.build}</p>
        </div>
      </footer>
    </div>
  );
}

function PlanCard({ name, price, cadence, badge, features, cta, onClick, highlighted, variant }) {
  const isContact = variant === 'contact';
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: highlighted ? '2px solid var(--accent)' : '1px solid var(--border)',
      borderRadius: 16, padding: '32px 28px', boxShadow: 'var(--shadow-md)',
      position: 'relative',
    }}>
      {badge && (
        <div style={{
          position: 'absolute', top: -12, right: 20,
          background: 'var(--accent)', color: '#1A3C6E',
          fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 12,
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>{badge}</div>
      )}
      <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 6 }}>{name}</h3>
      <div style={{ marginBottom: 24, minHeight: 56 }}>
        {isContact ? (
          <>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', lineHeight: 1.1 }}>{price}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>{cadence}</div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 44, fontWeight: 800, color: 'var(--text)' }}>{price}</span>
            <span style={{ fontSize: 15, color: 'var(--text-muted)' }}>{cadence}</span>
          </div>
        )}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, marginBottom: 24 }}>
        {features.map(f => (
          <li key={f} style={{ fontSize: 14, color: 'var(--text-muted)', padding: '6px 0', display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--success)' }}>✓</span> {f}
          </li>
        ))}
      </ul>
      <button onClick={onClick} style={{
        width: '100%', padding: '12px 0', borderRadius: 10, cursor: 'pointer',
        fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
        background: isContact ? 'transparent' : (highlighted ? 'var(--accent)' : 'var(--navy)'),
        color: isContact ? 'var(--heading)' : (highlighted ? '#1A3C6E' : '#fff'),
        border: isContact ? '1.5px solid var(--heading)' : 'none',
      }}>{cta}</button>
    </div>
  );
}

function ExtractMockup({ t }) {
  const rows = [
    { d: 'Premium HDMI cable 2m', p: 'RM 18.00',  s: 'CBL-HDMI-2',  q: 50  },
    { d: 'USB-C charger 65W',     p: 'RM 79.00',  s: 'CHG-USBC-65', q: 24  },
    { d: 'Bluetooth speaker',     p: 'RM 145.00', s: 'SPK-BT-01',   q: 12  },
    { d: 'Laptop sleeve 14"',     p: 'RM 42.00',  s: 'BAG-LP-14',   q: 30  },
  ];
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: 18, color: '#1a1a2e',
      boxShadow: '0 30px 80px rgba(0,0,0,0.18)', maxWidth: 460, margin: '0 auto',
      direction: 'ltr', border: '1px solid rgba(0,0,0,0.06)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        padding: '10px 12px', background: 'rgba(245,158,11,0.12)',
        borderRadius: 10, border: '1px dashed rgba(245,158,11,0.4)',
      }}>
        <span style={{ fontSize: 22 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1A3C6E' }}>supplier-pricelist-Q2.pdf</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Extracted by AI OCR · 4 items found</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#10B981', background: 'rgba(16,185,129,0.15)', padding: '3px 8px', borderRadius: 10 }}>● Ready</span>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '20px 2fr 1fr 1fr 0.6fr',
        gap: 6, padding: '6px 8px',
        background: '#1A3C6E', color: '#fff', borderRadius: 6,
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        <div></div><div>Item</div><div>SKU</div><div style={{ textAlign: 'right' }}>Price</div><div style={{ textAlign: 'right' }}>Qty</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '20px 2fr 1fr 1fr 0.6fr',
          gap: 6, padding: '8px 8px', borderBottom: '1px solid #f1f5f9',
          fontSize: 11, alignItems: 'center',
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: 3, background: '#F59E0B',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 10, fontWeight: 700,
          }}>✓</div>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.d}</div>
          <div style={{ color: '#64748b', fontFamily: 'DM Mono, monospace', fontSize: 10 }}>{r.s}</div>
          <div style={{ textAlign: 'right', fontWeight: 600 }}>{r.p}</div>
          <div style={{ textAlign: 'right', color: '#64748b' }}>{r.q}</div>
        </div>
      ))}

      <div style={{
        marginTop: 12, padding: '8px 12px', background: '#1A3C6E', color: '#fff',
        borderRadius: 8, fontSize: 11, fontWeight: 700, textAlign: 'center',
      }}>
        Save 4 items to catalogue →
      </div>
    </div>
  );
}

function InvoiceMockup() {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: 22, color: '#1a1a2e',
      boxShadow: '0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.05)',
      transform: 'rotate(-1.5deg)', maxWidth: 420, margin: '0 auto', direction: 'ltr',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Invoice</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1A3C6E', letterSpacing: '-0.5px', marginTop: 2 }}>INV-2026-0042</div>
        </div>
        <span style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>● Paid</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>From</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1A3C6E' }}>Alaganu Enterprise</div>
          <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.5 }}>Puchong, Selangor<br/>info@alaganu.com</div>
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Billed To</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1A3C6E' }}>Acme Sdn Bhd</div>
          <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.5 }}>Kuala Lumpur<br/>finance@acme.my</div>
        </div>
      </div>

      <div style={{ background: '#1A3C6E', color: '#fff', padding: '8px 12px', borderRadius: 6, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <div>Item</div><div style={{ textAlign: 'center' }}>Qty</div><div style={{ textAlign: 'right' }}>Amount</div>
      </div>
      {[
        { item: 'Web design service',  qty: 1, amt: 'RM 4,500.00' },
        { item: 'Hosting (12 months)', qty: 1, amt: 'RM 1,200.00' },
        { item: 'SSL certificate',     qty: 1, amt: 'RM   180.00' },
      ].map((r,i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, fontSize: 11, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', color: '#1a1a2e' }}>
          <div>{r.item}</div><div style={{ textAlign: 'center', color: '#64748b' }}>{r.qty}</div><div style={{ textAlign: 'right', fontWeight: 600 }}>{r.amt}</div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 12px 0', fontSize: 14, fontWeight: 800, color: '#1A3C6E' }}>
        <span>TOTAL</span><span>RM 5,880.00</span>
      </div>
    </div>
  );
}

const navInner = {
  maxWidth: 1100, margin: '0 auto', padding: '14px 24px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap',
};
const navLink = { fontSize: 14, fontWeight: 500, textDecoration: 'none', padding: 4 };
const ctaBtn = {
  background: '#F59E0B', color: '#1A3C6E', border: 'none', cursor: 'pointer',
  padding: '9px 18px', borderRadius: 8, fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
};
const heroCta = {
  background: '#F59E0B', color: '#1A3C6E', border: 'none', cursor: 'pointer',
  padding: '14px 24px', borderRadius: 10, fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
  boxShadow: '0 10px 30px rgba(245,158,11,0.4)',
};
const heroCtaGhost = {
  background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
  cursor: 'pointer', padding: '14px 24px', borderRadius: 10, fontWeight: 600, fontSize: 15,
  fontFamily: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
};
const eyebrow = {
  display: 'inline-block', padding: '4px 12px', background: 'rgba(46,117,182,0.12)',
  color: 'var(--blue)', fontSize: 12, fontWeight: 700, borderRadius: 16,
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14,
};
const sectionH  = { fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: 'var(--heading)', letterSpacing: '-1px', marginBottom: 12 };
const sectionSub = { fontSize: 16, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 560, margin: '0 auto' };
const footerH    = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-subtle)', marginBottom: 12 };
const footerLink = { display: 'block', fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none', padding: '4px 0' };
